import React, { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ChevronDown, Eye } from 'lucide-react'

export interface UploadedByUserOption {
  id: string
  name: string
  avatar?: string | null
}

interface Props {
  users: UploadedByUserOption[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
  currentUserId?: string
  onHideMineClick?: () => void
  showHideMineAction?: boolean
  hideMineActive?: boolean
  onClearAll?: () => void
  t?: (key: string) => string
  className?: string
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  triggerRef?: React.Ref<HTMLButtonElement>
}

// Reusable "Subidas por" dropdown with multi-select users
export const SortByUploadedByDropdown: React.FC<Props> = ({
  users,
  selected,
  onChange,
  currentUserId,
  onHideMineClick,
  showHideMineAction = false,
  hideMineActive = false,
  onClearAll,
  t,
  className,
  isOpen: externalIsOpen,
  onOpenChange,
  triggerRef
}) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = externalIsOpen !== undefined ? externalIsOpen : internalOpen
  const setOpen = onOpenChange || setInternalOpen
  const [pending, setPending] = useState<Set<string>>(new Set(selected))
  const visibleUsers = useMemo(() => {
    if (!hideMineActive || !currentUserId) return users
    return users.filter((user) => user.id !== currentUserId)
  }, [users, hideMineActive, currentUserId])
  const canHideMine = currentUserId ? !hideMineActive && !pending.has(currentUserId) : false

  const label = useMemo(() => {
    if (selected.size === 0) return t ? t('uploadedByAll') : 'Subidas por todos'
    if (selected.size === 1) {
      return t ? t('oneUser') : '1 usuario'
    }
    return `${selected.size} ${t ? t('users') : 'usuarios'}`
  }, [selected, t])

  const apply = () => {
    onChange(new Set(pending))
    setOpen(false)
  }
  const clear = () => {
    setPending(new Set())
    onChange(new Set())
    onClearAll?.()
    setOpen(false)
  }

  return (
    <div className={className}>
      <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) setPending(new Set(selected)); }}>
        <DropdownMenuTrigger asChild>
          <Button ref={triggerRef} variant="outline" size="sm" className="justify-between max-w-[170px] px-2 text-xs">
            {label}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-background border border-border z-30">
          {showHideMineAction && canHideMine && (
            <div className="p-2">
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  onHideMineClick?.()
                  setOpen(false)
                }}
                className={`h-auto w-full justify-end gap-1 p-0 text-right text-xs ${hideMineActive ? 'text-primary' : 'text-foreground'}`}
              >
                <Eye className="h-4 w-4 shrink-0" />
                <span className="text-right">{t ? t('hideMinePhotos') : 'Ocultar mías'}</span>
              </Button>
            </div>
          )}
          {showHideMineAction && canHideMine && <div className="border-t border-border my-1" />}

          {pending.size > 0 && (
            <div className="flex gap-2 p-2">
              <Button variant="outline" size="sm" onClick={clear} className="text-xs flex-1 hover:border-secondary">
                {t ? t('clear') : 'Limpiar'}
              </Button>
              <Button size="sm" onClick={apply} className="flex-1 text-xs">
                {t ? t('apply') : 'Aplicar'}
              </Button>
            </div>
          )}
          {pending.size > 0 && <div className="border-t border-border my-1" />}

          <div className="max-h-64 overflow-y-auto">
            {visibleUsers.map(user => (
              <DropdownMenuCheckboxItem
                key={user.id}
                checked={pending.has(user.id)}
                onCheckedChange={(checked) => {
                  setPending(prev => {
                    const next = new Set(prev)
                    if (checked) next.add(user.id); else next.delete(user.id)
                    return next
                  })
                }}
                className="flex items-center relative pl-8 pr-2 text-xs"
                onSelect={(e) => e.preventDefault()}
              >
                <div className="absolute left-2 w-4 h-4 border border-border rounded flex items-center justify-center bg-background">
                  {pending.has(user.id) && <div className="w-2 h-2 bg-primary rounded-sm" />}
                </div>
                <div className="flex items-center gap-2">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span>{user.name}</span>
                </div>
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
